import { Database, Challenge, Match } from '../types';
import { generateId } from './helpers';
import { db as firestore } from '../firebaseConfig';
import { doc, setDoc, updateDoc, getDoc, arrayUnion } from 'firebase/firestore';
import { createNotification } from './socialHelpers';

// Create a挑战 (Global)
export const createChallenge = async (
    _db: Database, // Unused but kept for signature compatibility if needed, though we change return type
    challengerId: string,
    challengerName: string,
    challengedId: string,
    challengedName: string,
    groupId: string,
    stakes?: string,
    deadline?: string,
    proposedDate?: string,
    challengerPartnerId?: string,
    location?: string
): Promise<void> => {
    const challengeId = `challenge_${Date.now()}`;
    const challenge: Challenge = {
        id: challengeId,
        challengerId,
        challengedId,
        groupId, // We still keep groupId for reference (e.g. which group context it was created in), but it lives globally
        stakes: stakes || null,
        deadline: deadline || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
        status: 'pending',
        createdAt: new Date().toISOString(),
        proposedDate: proposedDate || null,
        challengerPartnerId: challengerPartnerId || null,
        location: location || null,
        challengerPartnerStatus: challengerPartnerId ? 'pending' : null
    };

    try {
        await setDoc(doc(firestore, 'challenges', challengeId), challenge);

        // Notify challenged player
        const notif = await createNotification(
            () => { }, // No-op setDb
            groupId,
            challengedId,
            'challenge_new',
            `${challengerName} vous a lancé un défi${stakes ? ` : "${stakes}"` : ''} !`,
            'INBOX',
            { challengeId, fromPlayerId: challengerId },
            challengerId
        );

        if (notif) {
            try {
                const sentNotif = { ...notif, read: true };
                const cleanSentNotif = JSON.parse(JSON.stringify(sentNotif));
                await setDoc(doc(firestore, 'users', challengerId, 'notifications', notif.id), cleanSentNotif);
            } catch (e) { console.error(e); }
        }

        // Notify challenger's partner if selected
        if (challengerPartnerId) {
            await createNotification(
                () => { },
                groupId,
                challengerPartnerId,
                'challenge_new',
                `${challengerName} vous invite à être son partenaire pour un défi contre ${challengedName} !`,
                'INBOX',
                { challengeId, fromPlayerId: challengerId },
                challengerId
            );
        }
    } catch (error) {
        console.error("Error creating challenge:", error);
        throw error;
    }
};

// Accept a challenge (Global)
export const acceptChallenge = async (
    _db: Database,
    challengeId: string,
    accepterName: string,
    partnerId?: string
): Promise<void> => {
    try {
        const challengeRef = doc(firestore, 'challenges', challengeId);
        const challengeSnap = await getDoc(challengeRef);

        if (!challengeSnap.exists()) return;
        const challenge = challengeSnap.data() as Challenge;

        const updates: any = {
            status: 'accepted',
            challengedPartnerStatus: 'pending'
        };

        const is2v2 = !!challenge.challengerPartnerId;

        if (is2v2 && !partnerId) {
            // CRITICAL FIX: Prevent accepting 2v2 challenge without selecting a partner.
            console.error("Cannot accept 2v2 challenge without a partner.");
            throw new Error("Impossible d'accepter un défi 2v2 sans choisir de partenaire.");
        }

        if (partnerId) {
            updates.challengedPartnerId = partnerId;
            updates.challengedPartnerStatus = 'pending'; // Partner must confirm
        } else {
            // Only strictly valid for 1v1
            updates.challengedPartnerStatus = 'accepted';
        }

        await updateDoc(challengeRef, updates);

        // Notify challenger
        await createNotification(
            () => { },
            challenge.groupId,
            challenge.challengerId,
            'challenge_accepted',
            partnerId
                ? `${accepterName} a accepté le défi et a invité un partenaire.`
                : `${accepterName} a accepté le défi !`,
            'CHALLENGES',
            { challengeId },
            challenge.challengedId
        );

        // Notify chosen partner if any
        if (partnerId) {
            let challengerName = challenge.challengerId; // Fallback to ID
            try {
                // Fetch Challenger Profile to get Pseudo/Name
                const challengerSnap = await getDoc(doc(firestore, 'users', challenge.challengerId));
                if (challengerSnap.exists()) {
                    const data = challengerSnap.data();
                    challengerName = data.pseudo || data.name || challengerName;
                }
            } catch (e) {
                console.error("Error fetching challenger name for notification:", e);
            }

            await createNotification(
                () => { },
                challenge.groupId,
                partnerId,
                'challenge_new',
                `${accepterName} vous invite à être son partenaire pour un défi contre ${challengerName} !`,
                'INBOX',
                { challengeId },
                challenge.challengedId
            );
        } else {
            // Check if we can create match immediately
            const updatedChallenge = { ...challenge, ...updates };
            const challengerReady = !updatedChallenge.challengerPartnerId || updatedChallenge.challengerPartnerStatus === 'accepted';
            // challengedReady is true because we just accepted (and if no partner, status is accepted)

            if (challengerReady) {
                // Create Match
                const matchId = generateId('match');
                const newMatch: Match = {
                    id: matchId,
                    team1Id: updatedChallenge.challengerId,
                    team2Id: updatedChallenge.challengedId,
                    p3Id: updatedChallenge.challengerPartnerId || undefined,
                    p4Id: updatedChallenge.challengedPartnerId || undefined,
                    time: '12:00',
                    court: updatedChallenge.location || 'Lieu à définir',
                    status: 'scheduled',
                    score: [],
                    type: 'challenge',
                    date: updatedChallenge.proposedDate || new Date().toISOString().split('T')[0],
                    scoreSubmissions: {},
                    validationStatus: 'pending'
                };

                await updateDoc(challengeRef, {
                    status: 'completed',
                    matchId,
                    completedAt: new Date().toISOString()
                });

                // Add match to Group matches sub-collection (scalable)
                const matchRef = doc(firestore, 'groups', challenge.groupId, 'matches', matchId);
                await setDoc(matchRef, newMatch);

                // CRITICAL FIX: Add to Group document 'quickMatches' array for Global View visibility
                try {
                    await updateDoc(doc(firestore, 'groups', challenge.groupId), {
                        quickMatches: arrayUnion(newMatch)
                    });
                } catch (e) {
                    console.error("Failed to sync match to group array:", e);
                }

                // Notify everyone
                const playerIds = [updatedChallenge.challengerId, updatedChallenge.challengedId, updatedChallenge.challengerPartnerId, updatedChallenge.challengedPartnerId].filter(Boolean) as string[];
                const dateData = newMatch.date || new Date().toISOString();
                const formattedDate = new Date(dateData).toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
                for (const pid of playerIds) {
                    await createNotification(
                        () => { },
                        challenge.groupId,
                        pid,
                        'matchmaking_new',
                        `Le défi est confirmé ! Préparez-vous pour le match du ${formattedDate}.`,
                        'MATCHMAKING',
                        { matchId },
                        undefined
                    );
                }
            }
        }
    } catch (error) {
        console.error("Error accepting challenge:", error);
    }
};

// Respond to partner invite (Global)
export const respondToPartnerInvite = async (
    _db: Database,
    challengeId: string,
    playerId: string,
    response: 'accepted' | 'declined'
): Promise<void> => {
    try {
        const challengeRef = doc(firestore, 'challenges', challengeId);
        const challengeSnap = await getDoc(challengeRef);

        if (!challengeSnap.exists()) return;
        const challenge = challengeSnap.data() as Challenge;

        const updates: any = {};
        if (challenge.challengerPartnerId === playerId) {
            updates.challengerPartnerStatus = response;
        } else if (challenge.challengedPartnerId === playerId) {
            updates.challengedPartnerStatus = response;
        }

        await updateDoc(challengeRef, updates);

        // If declined, notify inviter
        if (response === 'declined') {
            const inviterId = challenge.challengerPartnerId === playerId ? challenge.challengerId : challenge.challengedId;
            await createNotification(
                () => { },
                challenge.groupId,
                inviterId,
                'system',
                `Votre partenaire a refusé l'invitation pour le défi.`,
                'CHALLENGES',
                undefined,
                playerId
            );
        } else {
            // Check if we can create match
            // We need to re-fetch or use updated local state logic. 
            // For simplicity, let's just check the conditions with the new response.
            const updatedChallenge = { ...challenge, ...updates };

            const challengerReady = !updatedChallenge.challengerPartnerId || updatedChallenge.challengerPartnerStatus === 'accepted';
            const challengedReady = updatedChallenge.status === 'accepted' && (!updatedChallenge.challengedPartnerId || updatedChallenge.challengedPartnerStatus === 'accepted');

            if (challengerReady && challengedReady) {
                // Create Match
                const matchId = generateId('match');
                const newMatch: Match = {
                    id: matchId,
                    team1Id: updatedChallenge.challengerId,
                    team2Id: updatedChallenge.challengedId,
                    p3Id: updatedChallenge.challengerPartnerId || undefined,
                    p4Id: updatedChallenge.challengedPartnerId || undefined,
                    time: '12:00',
                    court: updatedChallenge.location || 'Lieu à définir',
                    status: 'scheduled', // Changed from upcoming to planned
                    score: [],
                    type: 'challenge',
                    date: updatedChallenge.proposedDate || new Date().toISOString().split('T')[0],
                    scoreSubmissions: {},
                    validationStatus: 'pending'
                };

                await updateDoc(challengeRef, {
                    status: 'completed',
                    matchId,
                    completedAt: new Date().toISOString()
                });

                // Add match to Group matches sub-collection (scalable)
                const matchRef = doc(firestore, 'groups', challenge.groupId, 'matches', matchId);
                await setDoc(matchRef, newMatch);

                // CRITICAL FIX: Sync to Group array
                try {
                    await updateDoc(doc(firestore, 'groups', challenge.groupId), {
                        quickMatches: arrayUnion(newMatch)
                    });
                } catch (e) {
                    console.error("Failed to sync match to group array (partner response):", e);
                }

                // Notify everyone
                const playerIds = [updatedChallenge.challengerId, updatedChallenge.challengedId, updatedChallenge.challengerPartnerId, updatedChallenge.challengedPartnerId].filter(Boolean) as string[];
                const formattedDate = new Date(newMatch.date || new Date()).toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
                for (const pid of playerIds) {
                    await createNotification(
                        () => { },
                        challenge.groupId,
                        pid,
                        'matchmaking_new',
                        `Le défi est confirmé ! Préparez-vous pour le match du ${formattedDate}.`,
                        'MATCHMAKING',
                        { matchId },
                        undefined
                    );
                }
            }
        }
    } catch (error) {
        console.error("Error responding to partner invite:", error);
    }
};

// Propose new date (Global)
export const proposeNewDate = async (
    _db: Database,
    challengeId: string,
    proposerName: string,
    newDate: string
): Promise<void> => {
    try {
        const challengeRef = doc(firestore, 'challenges', challengeId);
        const challengeSnap = await getDoc(challengeRef);
        if (!challengeSnap.exists()) return;
        const challenge = challengeSnap.data() as Challenge;

        await updateDoc(challengeRef, {
            status: 'negotiating',
            proposedDate: newDate
        });

        await createNotification(
            () => { },
            challenge.groupId,
            challenge.challengerId,
            'challenge_new',
            `${proposerName} propose une nouvelle date : ${new Date(newDate).toLocaleDateString()}.`,
            'CHALLENGES',
            undefined,
            challenge.challengedId
        );
    } catch (error) {
        console.error("Error proposing new date:", error);
    }
};

// Decline challenge (Global)
export const declineChallenge = async (
    _db: Database,
    challengeId: string,
    declinerName: string,
    declinerId: string
): Promise<void> => {
    try {
        const challengeRef = doc(firestore, 'challenges', challengeId);
        const challengeSnap = await getDoc(challengeRef);
        if (!challengeSnap.exists()) return;
        const challenge = challengeSnap.data() as Challenge;

        await updateDoc(challengeRef, { status: 'declined' });

        // Notify all other participants
        const allParticipants = [
            challenge.challengerId,
            challenge.challengedId,
            challenge.challengerPartnerId,
            challenge.challengedPartnerId
        ].filter(id => id && id !== declinerId) as string[];

        for (const recipientId of allParticipants) {
            await createNotification(
                () => { },
                challenge.groupId,
                recipientId,
                'challenge_declined',
                `${declinerName} a refusé le défi.`,
                'INBOX',
                undefined,
                declinerId // context player (who declined)
            );
        }
    } catch (error) {
        console.error("Error declining challenge:", error);
    }
};

// Cancel challenge (Global)
export const cancelChallenge = async (
    _db: Database,
    challengeId: string,
    cancelerName: string,
    cancelerId: string
): Promise<void> => {
    try {
        const challengeRef = doc(firestore, 'challenges', challengeId);
        const challengeSnap = await getDoc(challengeRef);
        if (!challengeSnap.exists()) return;
        const challenge = challengeSnap.data() as Challenge;

        await updateDoc(challengeRef, { status: 'cancelled' });

        // Notify all other participants
        const allParticipants = [
            challenge.challengerId,
            challenge.challengedId,
            challenge.challengerPartnerId,
            challenge.challengedPartnerId
        ].filter(id => id && id !== cancelerId) as string[];

        for (const recipientId of allParticipants) {
            await createNotification(
                () => { },
                challenge.groupId,
                recipientId,
                'challenge_declined', // Use declined icon/type for cancellation
                `${cancelerName} a annulé le défi.`,
                'INBOX',
                undefined,
                cancelerId // Context player
            );
        }
    } catch (error) {
        console.error("Error cancelling challenge:", error);
    }
};

// Check deadlines (Global - Run periodically or on load)
// This function might be expensive if run often on client.
// We'll keep it simple: check loaded challenges in DB and update them if needed.
// But since we listen to challenges, we can just update the ones we see.
export const checkChallengeDeadlines = (_db: Database): Database => {
    // This function was synchronous and returned new DB.
    // Since we are now using Firestore listeners, we should probably just trigger updates to Firestore if we detect expired challenges.
    // But we shouldn't do this in a render loop.
    // For now, let's return db as is, and maybe add a separate async cleanup function.
    // Or just rely on the fact that we can't easily check all global challenges from client efficiently without a query.
    // We will skip auto-expiry for now to avoid complexity, or implement it as a separate action.
    return _db;
};

// Complete a challenge (Global)
export const completeChallenge = async (
    _db: Database,
    challengeId: string,
    matchId: string
): Promise<void> => {
    try {
        const challengeRef = doc(firestore, 'challenges', challengeId);
        await updateDoc(challengeRef, {
            status: 'completed',
            matchId,
            completedAt: new Date().toISOString()
        });
    } catch (error) {
        console.error("Error completing challenge:", error);
    }
};
